import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { getPaymentMethods } from "@endpoints/stripe.service";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import TickThinOutline from "@assets/svg/TickThinOutline";
import CardForm from "@components/Cards/Card/CardForm";
import { loadStripe } from "@stripe/stripe-js";
import env from "../../env";
import { Elements } from "@stripe/react-stripe-js";
import VisaCardImg from "@assets/images/Visa.svg";
import MaestroCardImage from "@assets/images/Maestro.png";
import AmericanExpressCardImg from "@assets/images/AmericanExpress.png";
import MasterCardImg from "@assets/images/MasterCard.png";
import dayjs from "@lib/dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import { purchaseSubscription } from "@endpoints/subscription.service";

const stripePromise = loadStripe(env.publishableKey);

const SubscriptionCheckoutPage = () => {
  const location = useLocation();
  const { priceId, amount, recurringType } = location?.state;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [cardLists, setCardLists] = useState([]);
  const subscriptionCheckoutRef = useRef(false);
  const [activeTab, setActiveTab] = useState("saved");
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState(null);
  const [endDate, setEndDate] = useState(dayjs());

  // fetch saved cards
  const fetchCards = () => {
    setIsLoading(true); // Ensure loading state is set

    getPaymentMethods("card")
      .then((response) => {
        if (response.success) {
          const cards = response.data;
          setCardLists(cards);

          // If we have cards but no selection, select the most recently added card
          if (cards.length > 0 && !selectedPaymentDetails) {
            // Cards are typically returned with newest first
            setSelectedPaymentDetails(cards[0]);
          }
        }
      })
      .catch((error) => enqueueSnackbar(error.message, { variant: "error" }))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (subscriptionCheckoutRef.current) return;
    // console.log("routeState", routeState);
    subscriptionCheckoutRef.current = true;
    if (recurringType === "month") {
      setEndDate(dayjs(dayjs().add(30, "days")));
    } else {
      setEndDate(dayjs(dayjs().add(1, "year")));
    }
    fetchCards();
  }, []);

  const handlePayment = (paymentMethodId) => {
    // Logic to handle payment with the selected payment method
    setIsPaymentLoading(true);
    console.log("Processing payment with method ID:", paymentMethodId);
    purchaseSubscription(priceId, paymentMethodId)
      .then((response) => {
        console.log("purchase subscription response", response);
        if (response.success) {
          enqueueSnackbar(
            "Your subscription has been activated successfully!",
            {
              variant: "success",
            }
          );
          navigate("/subscription");
        }
      })
      .catch((error) =>
        enqueueSnackbar("Error while purchasing subscription", {
          variant: "error",
        })
      )
      .finally(() => {
        setIsPaymentLoading(false);
      });
  };
  return (
    <div className="w-full h-9/10">
      <div className="bg-white w-full h-full max-w-6xl mx-auto rounded-lg shadow-sm md:shadow-md">
        <button
          className="flex items-center cursor-pointer p-4"
          onClick={() => {
            navigate(-1);
          }}
        >
          <ChevronLeftIcon className="w-4 h-4" fill="#4d4d4d" />
          <label className="text-xs font-medium text-primary">
            Back to plans
          </label>
        </button>
        <div className="flex flex-col md:flex-row justify-center items-stretch">
          {/* Left side - Pricing Details */}
          <div className="w-full md:w-2/5 relative px-6 md:px-10 py-8 md:py-10 bg-gray-50 rounded-t-lg md:rounded-t-none md:rounded-l-lg">
            <h2 className="text-2xl font-bold mb-6 text-secondary">
              Subscription Details
            </h2>
            <div className="space-y-5 md:space-y-6">
              <div className="bg-white p-5 md:p-8 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md">
                <div className="flex flex-col">
                  <span className="text-gray-500 text-sm mb-2">Plan Price</span>
                  <span className="text-2xl md:text-3xl font-bold text-secondary">
                    £{amount}{" "}
                    <span className="text-xs md:text-sm font-normal">
                      per {recurringType === "year" ? "year" : "month"}
                    </span>
                  </span>
                </div>
                <div className="mt-6 pt-5 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Billing cycle</span>
                    <span className="font-medium">
                      {recurringType === "year" ? "Yearly" : "Monthly"}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-3">
                    <span className="text-gray-500">Billing method</span>
                    <span className="font-medium">Card</span>
                  </div>
                  <div className="flex justify-between text-sm mt-3">
                    <span className="text-gray-500">Next billing date</span>
                    <span className="font-medium">
                      {endDate.format("MMMM D, YYYY")}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-orange-50 border border-orange-200 p-6 rounded-lg mt-8">
                <h3 className="font-semibold mb-2 text-secondary">
                  Subscription Terms
                </h3>
                <p className="text-sm text-secondary mb-2">
                  You'll be billed <strong>£{amount}</strong>{" "}
                  {recurringType === "year" ? "annually" : "monthly"}. Your
                  subscription will automatically renew unless canceled.
                </p>
                <p className="text-xs text-gray-500 mt-3">
                  By subscribing, you agree to our terms of service and
                  acknowledge our privacy policy.
                </p>
              </div>

              {/* <div className="mt-8 bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="text-blue-500 bg-blue-100 p-2 rounded-full mt-1">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-800">Need help?</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      If you have any questions about your subscription, our
                      support team is here to help.
                    </p>
                  </div>
                </div>
              </div> */}
            </div>
          </div>

          {/* Divider - Horizontal for mobile, Vertical for larger screens */}
          <div className="h-px md:h-auto md:w-px bg-gray-200 self-stretch"></div>

          {/* Right side - Payment Methods */}
          <div className="w-full md:w-3/5 px-6 md:px-10 py-8 md:py-10 rounded-lg md:rounded-b-none md:rounded-r-lg">
            <h2 className="text-2xl font-bold mb-6 text-secondary">
              Payment Method
            </h2>
            <div className="flex space-x-1 md:space-x-2 mb-6 md:mb-8 bg-gray-100 p-1 rounded-full w-fit">
              <button
                onClick={() => setActiveTab("saved")}
                className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base rounded-full transition-all duration-300 font-medium ${
                  activeTab === "saved"
                    ? "bg-secondary text-white shadow-md"
                    : "bg-transparent text-gray-600 hover:bg-gray-200"
                }`}
              >
                Saved Cards
              </button>
              <button
                onClick={() => setActiveTab("new")}
                className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base rounded-full transition-all duration-300 font-medium ${
                  activeTab === "new"
                    ? "bg-secondary text-white shadow-md"
                    : "bg-transparent text-gray-600 hover:bg-gray-200"
                }`}
              >
                Add New Card
              </button>
            </div>

            {/* Content based on active tab */}
            {activeTab === "saved" ? (
              isLoading ? (
                <div className="gap-2.5 flex mt-4 flex-col">
                  {Array.from({ length: 3 }, (x, i) => i).map((key) => (
                    <div key={key} className="flex justify-between">
                      <div className="flex rounded-lg w-full pl-3 pr-8 py-4 gap-1.5 bg-gray-100">
                        <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                        <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                        <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : cardLists.length > 0 ? (
                <div className="space-y-3">
                  {cardLists.map((card, index) => {
                    const isSelected = selectedPaymentDetails?.id === card?.id;
                    return (
                      <div
                        key={card.id}
                        onClick={() => {
                          if (selectedPaymentDetails?.id === card?.id) {
                            setSelectedPaymentDetails(null);
                          } else {
                            setSelectedPaymentDetails(card);
                          }
                        }}
                        className={`
                          border rounded-lg p-5 cursor-pointer transition-all duration-300
                          ${
                            isSelected
                              ? "border-secondary bg-orange-50 shadow-md transform scale-[1.02]"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }
                        `}
                      >
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-4 sm:gap-10">
                          <div className="flex items-center gap-3">
                            <div
                              className={
                                isSelected
                                  ? "w-5 h-5 rounded-md border-2 border-secondary"
                                  : "w-5 h-5 rounded-md border-2"
                              }
                            >
                              {isSelected ? (
                                <TickThinOutline
                                  className={"w-4 h-4 animate-fadeIn"}
                                  fill="#FF9800"
                                />
                              ) : null}
                            </div>
                            <img
                              src={(function () {
                                switch (card?.card?.brand.toLowerCase()) {
                                  case "visa":
                                    return VisaCardImg;
                                  case "mastercard":
                                    return MasterCardImg;
                                  case "amex":
                                    return AmericanExpressCardImg;
                                  case "maestro":
                                    return MaestroCardImage;
                                  default:
                                    return VisaCardImg;
                                }
                              })()}
                              alt={card?.card?.brand}
                              className="h-6"
                            />
                            <span className="text-sm text-primary">
                              **** **** **** {card?.card?.last4}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 ml-8 sm:ml-0 mt-2 sm:mt-0">
                            <span className="text-sm text-gray-600">
                              {card?.card?.exp_month}/{card?.card?.exp_year}
                            </span>
                            <span className="text-sm text-gray-600">
                              {card?.billing_details?.name}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {selectedPaymentDetails && (
                    <button
                      className="flex items-center justify-center bg-secondary text-white rounded-lg py-2.5 md:py-3 text-sm md:text-base font-semibold mt-6 md:mt-8 px-6 md:px-8 w-full transition-all duration-300 hover:bg-orange-600 transform hover:scale-[1.02]"
                      onClick={() => {
                        handlePayment(selectedPaymentDetails.id);
                      }}
                    >
                      {isPaymentLoading ? (
                        <>
                          <LoadingAnimation
                            color={"#FFFFFF"}
                            className="w-4 h-4 md:w-5 md:h-5 mr-2"
                          />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <span>Subscribe Now • £{amount}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-10 md:py-16 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <svg
                    className="w-12 h-12 md:w-16 md:h-16 mx-auto text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    ></path>
                  </svg>
                  <p className="text-gray-500 mt-4 mb-2">
                    No saved cards found.
                  </p>
                  <p className="text-gray-400 text-sm mb-4 md:mb-6 px-2">
                    Add a payment method to continue with subscription
                  </p>
                  <button
                    onClick={() => setActiveTab("new")}
                    className="mt-2 bg-secondary text-white rounded-lg py-2.5 md:py-3 text-sm md:text-base font-medium px-5 md:px-6 transition-all duration-300 hover:bg-orange-600"
                  >
                    Add a new card
                  </button>
                </div>
              )
            ) : (
              <Elements stripe={stripePromise}>
                <div className="max-w-full">
                  <CardForm
                    sendCancel={() => {
                      setActiveTab("saved");
                      fetchCards();
                    }}
                    onCardSaved={() => {
                      setActiveTab("saved");
                      setIsLoading(true); // Show loader while fetching cards
                      fetchCards(); // This will update the card list and set isLoading to false when done
                    }}
                  />
                </div>
              </Elements>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckoutPage;
